import React, { useState } from 'react';
import { Calendar, Coins, FileText, User, Trash2, Eye, Copy, Download, ExternalLink } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Receipts } from '@/integrations/api';
import { ReceiptModal } from './ReceiptModal';
import { expenseTypeLabel, paymentMethodLabel } from '@/lib/expenseOptions';

export interface Expense {
  id: string;
  amount: number;
  description: string;
  expenseType?: 'frokost' | 'mad_paa_lokation' | 'mad_vaek_fra_lokation' | 'faelles_udlaeg' | 'transport' | 'andet';
  paymentMethod?: 'privat' | 'firmakort';
  date: string;
  submittedAt?: string;
  paidBy: string;
  status: 'pending' | 'recorded' | 'paid' | 'rejected';
  receiptUrl?: string;
  currency: string;
  participants?: string;
  rejectionReason?: string;
}

interface ExpenseCardProps {
  expense: Expense;
  onEdit?: (expense: Expense) => void;
  onStatusChange?: (id: string, status: Expense['status']) => void;
  onDelete?: (id: string) => void;
  onReject?: (expense: Expense) => void;
  showActions?: boolean;
  isAdminView?: boolean;
}

export const statusConfig = {
  pending: {
    label: 'Not Yet Recorded',
    variant: 'warning' as const,
    className: 'bg-warning text-warning-foreground'
  },
  recorded: {
    label: 'Recorded',
    variant: 'default' as const,
    className: 'bg-status-recorded text-primary-foreground'
  },
  paid: {
    label: 'Paid Out',
    variant: 'success' as const,
    className: 'bg-status-paid text-success-foreground'
  },
  rejected: {
    label: 'Rejected',
    variant: 'destructive' as const,
    className: 'bg-destructive text-destructive-foreground'
  }
};

export function ExpenseCard({ 
  expense, 
  onEdit, 
  onStatusChange, 
  onDelete,
  onReject,
  showActions = false,
  isAdminView = false 
}: ExpenseCardProps) {
  const statusInfo = statusConfig[expense.status];
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const isInTeams = window.navigator.userAgent.includes('Teams');

  const handleDownloadReceipt = async () => {
    if (!expense.receiptUrl) return;
    let blobUrl: string | null = null;
    try {
      blobUrl = await Receipts.objectUrl(expense.receiptUrl);

      if (isMobile || isInTeams) {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `receipt-${expense.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download receipt');
    } finally {
      // Mobile/Teams opens the blob URL in a new tab, so only revoke for the
      // desktop download path where it has already been consumed by the click.
      if (blobUrl && !(isMobile || isInTeams)) {
        window.URL.revokeObjectURL(blobUrl);
      }
    }
  };

  const handleViewReceipt = () => {
    if (expense.receiptUrl) {
      // Both admin and employee views use the modal approach for better UX.
      setReceiptModalOpen(true);
    }
  };

  return (
    <Card className="hover:shadow-medium transition-all duration-200 animate-fade-in">
      {/* Mobile-optimized header - more compact */}
      <CardHeader className="pb-2 md:pb-3">
        <div className="flex flex-col gap-1">
          <div className="flex justify-start md:hidden">
            <Badge className={`${statusInfo.className} text-xs`}>
              {statusInfo.label}
            </Badge>
          </div>
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2 flex-shrink-0">
              <Coins className="h-4 w-4 md:h-5 md:w-5 text-accent flex-shrink-0" />
              <span className="text-lg md:text-xl font-semibold text-foreground whitespace-nowrap">
                {expense.currency} {expense.amount.toFixed(2)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0"
                onClick={() => {
                  navigator.clipboard.writeText(expense.amount.toFixed(2));
                  toast.success('Amount copied');
                }}
              >
                <Copy className="h-3 w-3 text-muted-foreground" />
              </Button>
            </div>
            <Badge className={`${statusInfo.className} text-xs flex-shrink-0 hidden md:inline-flex`}>
              {statusInfo.label}
            </Badge>
          </div>
        </div>
        
        {/* Mobile: Show description directly in header */}
        <div className="md:hidden">
          <div className="flex items-start gap-1 text-muted-foreground">
            <FileText className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span className="text-sm line-clamp-2 flex-1">{expense.description}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 flex-shrink-0"
              onClick={() => {
                const text = [expense.description, expense.participants].filter(Boolean).join(' ');
                navigator.clipboard.writeText(text);
                toast.success('Copied to clipboard');
              }}
            >
              <Copy className="h-3 w-3 text-muted-foreground" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-2 md:space-y-3">
        {/* Expense type + payment method */}
        {(expense.expenseType || expense.paymentMethod) && (
          <div className="flex flex-wrap gap-1.5">
            {expense.expenseType && (
              <Badge variant="outline">{expenseTypeLabel(expense.expenseType)}</Badge>
            )}
            {expense.paymentMethod && (
              <Badge variant={expense.paymentMethod === 'firmakort' ? 'default' : 'secondary'}>
                {paymentMethodLabel(expense.paymentMethod)}
              </Badge>
            )}
          </div>
        )}

        {/* Desktop: Show full details */}
        <div className="hidden md:block">
          <div className="grid grid-cols-1 gap-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <FileText className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm flex-1">{expense.description}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0"
                onClick={() => {
                  const text = [expense.description, expense.participants].filter(Boolean).join(' ');
                  navigator.clipboard.writeText(text);
                  toast.success('Copied to clipboard');
                }}
              >
                <Copy className="h-3 w-3 text-muted-foreground" />
              </Button>
            </div>
            
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="h-4 w-4" />
              <span className="text-sm">Paid by {expense.paidBy}</span>
            </div>
            
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">
                {new Date(expense.date).toLocaleDateString()}
              </span>
            </div>
            
            {expense.participants && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="h-4 w-4" />
                <span className="text-sm">Participants: {expense.participants}</span>
              </div>
            )}
            
            {expense.rejectionReason && (
              <div className="mt-2 p-2 rounded-md bg-destructive/10 border border-destructive/20">
                <p className="text-xs font-medium text-destructive mb-1">Rejection Reason:</p>
                <p className="text-xs text-muted-foreground">{expense.rejectionReason}</p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile: Compact details in one line */}
        <div className="md:hidden">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <span className="truncate">{expense.paidBy}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{new Date(expense.date).toLocaleDateString()}</span>
            </div>
          </div>
          {expense.participants && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <User className="h-3 w-3" />
              <span className="truncate">Participants: {expense.participants}</span>
            </div>
          )}
          {expense.rejectionReason && (
            <div className="mt-2 p-2 rounded-md bg-destructive/10 border border-destructive/20">
              <p className="text-xs font-medium text-destructive mb-1">Rejection Reason:</p>
              <p className="text-xs text-muted-foreground">{expense.rejectionReason}</p>
            </div>
          )}
        </div>

        {showActions && (
          <div className="flex gap-2 pt-2 flex-wrap">
            {(expense.status === 'pending' || expense.status === 'rejected') && onEdit && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onEdit(expense)}
              >
                Edit
              </Button>
            )}
            
            {(expense.status === 'pending' || expense.status === 'rejected') && onDelete && !isAdminView && (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => onDelete(expense.id)}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            )}
            
            {isAdminView && onStatusChange && (
              <div className="flex gap-1 flex-wrap">
                {expense.status === 'pending' && (
                  <>
                    <Button 
                      variant="default" 
                      size="sm"
                      onClick={() => onStatusChange(expense.id, 'recorded')}
                    >
                      Record
                    </Button>
                    {onReject && (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => onReject(expense)}
                      >
                        Reject
                      </Button>
                    )}
                  </>
                )}
                {expense.status === 'recorded' && (
                  <>
                    <Button 
                      variant="success" 
                      size="sm"
                      onClick={() => onStatusChange(expense.id, 'paid')}
                    >
                      Mark Paid
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onStatusChange(expense.id, 'pending')}
                    >
                      Undo Record
                    </Button>
                  </>
                )}
                {expense.status === 'paid' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => onStatusChange(expense.id, 'recorded')}
                  >
                    Revert Payment
                  </Button>
                )}
              </div>
            )}
            
            {expense.receiptUrl && (
              <>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleViewReceipt}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  View Receipt
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleDownloadReceipt}
                >
                  {isMobile || isInTeams ? (
                    <>
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Open Receipt
                    </>
                  ) : (
                    <>
                      <Download className="h-3 w-3 mr-1" />
                      Download
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
      
      <ReceiptModal
        open={receiptModalOpen}
        onClose={() => setReceiptModalOpen(false)}
        receiptUrl={expense.receiptUrl || null}
        expenseId={expense.id}
      />
    </Card>
  );
}